import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Alert, Stack, Typography, CircularProgress,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { ValidationError, toFormErrors } from '@/lib/validationErrors';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { OrgUnitTreeSelect } from '@/components/forms/OrgUnitTreeSelect';
import { useEnumValueOptions } from '@/lib/enumGridFilters';
import { useOrgRoleFunctions } from '@/hooks/core/useOrgRoleFunction';

interface GenerateResult {
  createdHeaderIds: string[];
  skippedCount: number;
}

interface Props {
  open: boolean;
  /** The header the dialog runs on — it acts as the prototype that every generated header copies. */
  sourceHeaderId: string;
  onClose: () => void;
  onGenerated?: (result: GenerateResult) => void;
}

/**
 * Bespoke dialog for "generate one PlanHeader per OrgUnit node from this header" (the prototype).
 * The generated command actions-menu can't host a lookup + enum selects, so per the generation spec
 * this dialog calls PlanHeaderAppService.GenerateForOrgTreeAsync directly. Idempotent on the server.
 */
export function GenerateForOrgTreeDialog({ open, sourceHeaderId, onClose, onGenerated }: Props) {
  const { t } = useTranslation();
  const [targetRootOrgUnitId, setTargetRootOrgUnitId] = useState<string | null>(null);
  const [targetMode, setTargetMode] = useState('ALL_DESCENDANTS');
  const [targetOrgType, setTargetOrgType] = useState('');
  const [assignedToRoleFunction, setAssignedToRoleFunction] = useState('');
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [result, setResult] = useState<GenerateResult | null>(null);

  const { options: modeOptions } = useEnumValueOptions('OrgTreeTargetMode');
  const { options: orgTypeOptions } = useEnumValueOptions('OrgUnitType');
  const { data: roleFunctions } = useOrgRoleFunctions();

  const reset = () => {
    setTargetRootOrgUnitId(null);
    setTargetMode('ALL_DESCENDANTS');
    setTargetOrgType('');
    setAssignedToRoleFunction('');
    setErrors([]);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<GenerateResult>('/api/finance/planheader/generate-for-org-tree', {
        sourceHeaderId,
        targetRootOrgUnitId,
        targetMode,
        targetOrgType: targetOrgType || null,
        assignedToRoleFunction,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setErrors([]);
      setResult(data);
      onGenerated?.(data);
    },
    onError: (error: unknown) => {
      setErrors(toFormErrors(error, t('Finance.GenerateHeaders.Failed', { defaultValue: 'Generation failed.' })));
    },
  });

  const canSubmit = !!targetRootOrgUnitId && assignedToRoleFunction.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Finance.GenerateHeaders.Title', { defaultValue: 'Generate plan headers from this header' })}</DialogTitle>
      <DialogContent>
        <DialogValidationErrors errors={errors} onClear={() => setErrors([])} />

        {result ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            {t('Finance.GenerateHeaders.Success', {
              defaultValue: 'Created {{created}} header(s); skipped {{skipped}} that already existed.',
              created: result.createdHeaderIds.length,
              skipped: result.skippedCount,
            })}
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('Finance.GenerateHeaders.Body', {
                defaultValue:
                  "This header is the prototype. One header is created per organisation node under the chosen root, " +
                  "copying this header's version, type, template, period range and scope-pins. Re-running only fills " +
                  'nodes that have no header yet.',
              })}
            </Typography>
            <OrgUnitTreeSelect
              value={targetRootOrgUnitId}
              onChange={setTargetRootOrgUnitId}
              label={t('Finance.GenerateHeaders.RootLabel', { defaultValue: 'Root organisation unit' })}
              required
            />
            <TextField
              select
              label={t('Finance.GenerateHeaders.ScopeLabel', { defaultValue: 'Scope' })}
              value={targetMode}
              onChange={(e) => setTargetMode(e.target.value)}
              fullWidth
            >
              {modeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField
              select
              label={t('Finance.GenerateHeaders.OrgTypeLabel', { defaultValue: 'Only nodes of type (optional)' })}
              value={targetOrgType}
              onChange={(e) => setTargetOrgType(e.target.value)}
              fullWidth
            >
              <MenuItem value=""><em>{t('Finance.GenerateHeaders.AnyType', { defaultValue: 'Any type' })}</em></MenuItem>
              {orgTypeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField
              select
              label={t('Finance.GenerateHeaders.RoleFunctionLabel', { defaultValue: 'Contributor role function' })}
              value={assignedToRoleFunction}
              onChange={(e) => setAssignedToRoleFunction(e.target.value)}
              required
              fullWidth
              helperText={t('Finance.GenerateHeaders.RoleFunctionHelp', {
                defaultValue: "Each generated header's 'Assigned to' becomes this role on its own node.",
              })}
            >
              {(roleFunctions ?? [])
                .filter((f) => f.isActive)
                .map((f) => <MenuItem key={f.code} value={f.code}>{f.name}</MenuItem>)}
            </TextField>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {result
            ? t('common.close', { defaultValue: 'Close' })
            : t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        {!result && (
          <Button variant="contained" onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? <CircularProgress size={20} /> : t('Finance.GenerateHeaders.Generate', { defaultValue: 'Generate' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
